<template>
    <div class="section-management-container container">
      <el-card shadow="hover">
        <template #header>
          <div class="card-header">
            <h2>إدارة الأقسام (الشُعب)</h2>
            <el-button type="primary" @click="handleCreate">
              <el-icon><Plus /></el-icon> إضافة قسم جديد
            </el-button>
          </div>
        </template>
  
        <el-alert
          title="ما هي هذه الواجهة؟"
          type="info"
          description="هذه الواجهة تسمح لك بتقسيم الفصول الدراسية إلى أقسام أو شُعب أصغر (مثال: A, B, C). هذا يساعد في تنظيم الطلاب وتوزيعهم بشكل أفضل."
          :closable="false"
          show-icon
          style="margin-bottom: 20px;"
        />
  
        <!-- --- قسم الفلاتر الجديد --- -->
        <div class="filter-section">
          <el-select v-model="filters.study_year_id" placeholder="فلترة حسب السنة الدراسية" clearable @change="handleYearFilterChange" style="width: 240px;">
            <el-option v-for="year in studyYears" :key="year.id" :label="year.name" :value="year.id" />
          </el-select>
          <el-select v-model="filters.class_id" placeholder="فلترة حسب الفصل" clearable @change="fetchSections" style="width: 240px;">
            <el-option v-for="classItem in filteredClasses" :key="classItem.id" :label="classItem.name" :value="classItem.id" />
          </el-select>
        </div>
  
        <el-table :data="sections" v-loading="loading" border style="width: 100%">
          <el-table-column prop="name" label="اسم القسم" />
          <el-table-column label="الفصل الدراسي">
            <template #default="{ row }">
              {{ row.class ? row.class.name : 'N/A' }}
            </template>
          </el-table-column>
          <el-table-column label="السنة الدراسية">
            <template #default="{ row }">
              {{ row.class && row.class.study_year ? row.class.study_year.name : 'N/A' }}
            </template>
          </el-table-column>
          <el-table-column label="الإجراءات" width="150" fixed="right">
            <template #default="{ row }">
              <el-button size="small" @click="handleEdit(row)">تعديل</el-button>
              <el-popconfirm
                title="هل أنت متأكد من حذف هذا القسم؟"
                confirm-button-text="نعم"
                cancel-button-text="لا"
                @confirm="handleDelete(row.id)"
              >
                <template #reference><el-button size="small" type="danger">حذف</el-button></template>
              </el-popconfirm>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
  
      <!-- Dialog لإنشاء/تعديل القسم -->
      <el-dialog v-model="dialogVisible" :title="dialogTitle" width="40%" :close-on-click-modal="false">
        <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
          <el-form-item label="اسم القسم" prop="name">
            <el-input v-model="form.name" placeholder="مثال: A" />
          </el-form-item>
          <el-form-item label="الفصل الدراسي" prop="class_id">
            <el-select v-model="form.class_id" placeholder="اختر الفصل الدراسي" style="width: 100%;">
              <el-option 
                v-for="classItem in classes" 
                :key="classItem.id" 
                :label="`${classItem.name} (${classItem.study_year ? classItem.study_year.name : ''})`" 
                :value="classItem.id" 
              />
            </el-select>
          </el-form-item>
        </el-form>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="dialogVisible = false">إلغاء</el-button>
            <el-button type="primary" @click="submitForm" :loading="formLoading">
              {{ form.id ? 'تحديث' : 'إنشاء' }}
            </el-button>
          </span>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { ElMessage, ElPopconfirm } from 'element-plus';
  import { Plus } from '@element-plus/icons-vue';
  
  export default {
    name: 'SectionManagement',
    components: { Plus, ElPopconfirm },
    data() {
      return {
        api: null,
        loading: true,
        sections: [],
        classes: [],
        studyYears: [],
        filters: {
          study_year_id: null,
          class_id: null,
        },
        dialogVisible: false,
        formLoading: false,
        form: this.getInitialForm(),
        rules: {
          name: [{ required: true, message: 'اسم القسم مطلوب', trigger: 'blur' }],
          class_id: [{ required: true, message: 'الفصل الدراسي مطلوب', trigger: 'change' }],
        },
      };
    },
    computed: {
      dialogTitle() {
        return this.form.id ? 'تعديل قسم' : 'إضافة قسم جديد';
      },
      filteredClasses() {
        if (!this.filters.study_year_id) {
          return this.classes;
        }
        return this.classes.filter(c => c.study_year_id === this.filters.study_year_id);
      }
    },
    created() {
      this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
      this.api.interceptors.request.use((config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      });
      this.fetchSections();
      this.fetchFilterData();
    },
    methods: {
      getInitialForm() {
        return { id: null, name: '', class_id: null };
      },
      async fetchSections() {
        this.loading = true;
        try {
          const response = await this.api.get('/sections', { params: this.filters });
          this.sections = response.data;
        } catch (error) {
          ElMessage.error('فشل في جلب الأقسام.');
        } finally {
          this.loading = false;
        }
      },
      async fetchFilterData() {
        try {
          const [classesRes, yearsRes] = await Promise.all([
            this.api.get('/classes'),
            this.api.get('/study-years'),
          ]);
          this.classes = classesRes.data;
          this.studyYears = yearsRes.data.data || yearsRes.data;
        } catch (error) {
          ElMessage.error('فشل في جلب بيانات الفلاتر.');
        }
      },
      handleYearFilterChange() {
        this.filters.class_id = null; // Reset class filter when year changes
        this.fetchSections();
      },
      handleCreate() {
        this.form = this.getInitialForm();
        this.dialogVisible = true;
      },
      handleEdit(section) {
        this.form = { ...section };
        this.dialogVisible = true;
      },
      async submitForm() {
        if (!this.$refs.formRef) return;
        await this.$refs.formRef.validate(async (valid) => {
          if (!valid) return;
          this.formLoading = true;
          try {
            if (this.form.id) {
              await this.api.put(`/sections/${this.form.id}`, this.form);
              ElMessage.success('تم تحديث القسم بنجاح!');
            } else {
              await this.api.post('/sections', this.form);
              ElMessage.success('تم إنشاء القسم بنجاح!');
            }
            this.dialogVisible = false;
            this.fetchSections();
          } catch (error) {
            const errorMsg = error.response?.data?.errors ? Object.values(error.response.data.errors)[0][0] : (error.response?.data?.message || 'فشلت العملية.');
            ElMessage.error(errorMsg);
          } finally {
            this.formLoading = false;
          }
        });
      },
      async handleDelete(id) {
        try {
          await this.api.delete(`/sections/${id}`);
          ElMessage.success('تم حذف القسم بنجاح!');
          this.fetchSections();
        } catch (error) {
          ElMessage.error(error.response?.data?.message || 'فشل في حذف القسم.');
        }
      },
    },
  };
  </script>
  
  <style scoped>
  .section-management-container {
    padding: 20px;
  }
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .filter-section {
    display: flex;
    gap: 15px;
    margin-bottom: 20px;
  }
  </style>
  